import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  atualizarRequisicoes,
  criarCotacao,
  fetchCotacoes,
  fetchFilaAprovacoes,
  fetchFornecedores,
  fetchItensEmLote,
  fetchPresets,
  fetchSolicitacoesAprovadas,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import {
  CotacaoListItem,
  CotacaoStatus,
  Fornecedor,
  Preset,
  Solicitacao,
} from '../../../interfaces/PortalCotacoes';
import { CotacaoBadge, PrazoVencidoBadge, prazoVencido } from '../components/PortalBadges';
import ConfirmacaoModal from '../components/ConfirmacaoModal';
import {
  CotacaoDoItem,
  cotacoesDaCadeia,
  cotacoesDaSolicitacao,
  estaEmAndamento,
  itensJaCotadosNaCadeia,
  versoesNaoCotadasDaCadeia,
} from '../cadeiaSolicitacao';
import { colunasDeReferencias } from '../components/Referencias';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import IconeAlerta from '../components/IconeAlerta';
import FornecedorDetalhesModal from '../components/FornecedorDetalhesModal';
import EscolhaDeItensModal from '../components/EscolhaDeItensModal';
import { notify } from '../../../shared/feedback/notify';
import { PORTAL_COTACOES_APROVACOES, PORTAL_COTACOES_SUPERVISAO } from '../routes';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import SearchIcon from '../../../img/search-icon.svg';
import FilterIcon from '../../../img/filter.svg';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../recepcao/pages/Recepcao.css';
import './PortalCotacoes.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import '../../../shared/feedback/erro-de-tela.css';


export const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'De onde vem uma cotação, e qual é o número dela',
    conteudo: (
      <div>
        <p>
          As cotações nascem de uma <strong>requisição de compra aprovada</strong> no módulo de{' '}
          <strong>Requisições</strong>: quando o gestor aprova a requisição no Nasajon, ela aparece aqui para
          ser cotada.
        </p>
        <p>
          <strong>Uma requisição, uma cotação.</strong> Cada requisição aprovada vira a sua própria cotação, e o
          número da cotação é o <strong>número do processo no Nasajon</strong> — o mesmo que a área de Compras vê
          no ERP, para não haver dois números para a mesma compra.
        </p>
        <p>
          Ao criar, o sistema <strong>confere de novo</strong> se a requisição continua aprovada: se ela foi
          reprovada ou cancelada depois de a lista carregar, a criação é recusada, o motivo aparece e a lista é
          atualizada. Uma requisição só pode ter <strong>uma cotação em andamento</strong>; quando a anterior é
          cancelada ou concluída, a nova recebe um sufixo (-2, -3) para os dois documentos não se confundirem.
        </p>
      </div>
    ),
  },
  {
    titulo: 'O botão Atualizar',
    conteudo: (
      <p>
        O primeiro passo da nova cotação mostra as requisições <strong>aprovadas</strong>. A aprovação acontece
        no Nasajon e o sistema a confere de tempos em tempos; se você acabou de aprovar e ela não apareceu, use{' '}
        <strong>Atualizar</strong>, que pergunta ao Nasajon na hora. O botão aceita um clique a cada 30 segundos.
      </p>
    ),
  },
  {
    titulo: 'Complementos: você cota só o que faltou',
    conteudo: (
      <div>
        <p>
          Um orçamento pode ganhar <strong>complementos</strong> (mais peças, ou mais quantidade de uma peça). Cada
          versão gera a sua própria requisição, e a lista traz{' '}
          <strong>apenas o que aquela versão pediu</strong> — o que já estava no orçamento anterior não volta para
          ser cotado de novo. Quando a quantidade de uma peça aumentou, você cota{' '}
          <strong>só o acréscimo</strong>, e a tela diz isso no próprio item (o fornecedor também lê esse aviso).
        </p>
        <p>
          Requisição que já virou cotação recebe o selo <strong>Já cotada</strong> — ela continua disponível,
          porque recotar às vezes é intencional (peça que ficou sem vencedor, prazo estourado). E se outra versão
          do mesmo orçamento estiver aprovada sem nunca ter sido cotada, a tela avisa.
        </p>
        <p>
          <strong>Cada versão vira a sua própria cotação.</strong> Para cotar a inicial e um complemento, crie
          duas cotações — cada uma com o número do seu processo no Nasajon. Escolher outra requisição no primeiro
          passo <strong>substitui</strong> a escolha; clicar na que já está marcada a desmarca.
        </p>
        <p>
          Quando o orçamento já tem cotação em outra versão, a tabela de itens marca{' '}
          <strong>quais peças já estão cotadas e em qual cotação</strong>. Se essa conferência não puder ser
          feita, a tela avisa que não conseguiu — ausência de marcação não é garantia de que não há repetição.
        </p>
      </div>
    ),
  },
  {
    titulo: 'As duas referências de preço',
    conteudo: (
      <p>
        Nos itens, no detalhe da proposta e na comparação aparecem duas referências, sempre identificadas:{' '}
        <strong>Orçado (Cilia)</strong> é o valor unitário que veio do orçamento da oficina;{' '}
        <strong>Média de compras (ERP)</strong> é o que a Atual pagou por aquela peça nas compras recentes, com o
        número de compras consideradas. Elas ficam <strong>congeladas</strong> no momento em que a cotação é
        criada — mexer no orçamento depois não altera a cotação já enviada. São informação{' '}
        <strong>interna</strong>: o fornecedor nunca as vê.
      </p>
    ),
  },
  {
    titulo: 'Contatos do fornecedor',
    conteudo: (
      <p>
        Os contatos <strong>não vêm do ERP</strong>: o cadastro de fornecedores do Nasajon não tem campo de
        telefone nem de e-mail, então quem os cadastra é a área de Compras. Dá para fazer isso{' '}
        <strong>no próprio passo de fornecedores</strong> da nova cotação, pelo botão do card — sem abandonar o
        que você já preencheu. <strong>O convite sai só pelo WhatsApp</strong> (decisão do teste com Compras,
        29/10/2026): fornecedor sem telefone cadastrado aparece desabilitado até alguém cadastrar o número.
      </p>
    ),
  },
  {
    titulo: 'Itens por fornecedor',
    conteudo: (
      <p>
        Todo fornecedor marcado recebe <strong>todos os itens</strong>. Para mandar só parte deles (o fornecedor
        especializado, que não responde o orçamento inteiro), use o botão <strong>Itens</strong> no card dele: o
        fornecedor vê e cota só as peças marcadas, e as outras nem aparecem para ele. Cada peça precisa ir para
        ao menos um fornecedor; enquanto alguma ficar sem ninguém, a tela não avança e diz qual é.
      </p>
    ),
  },
  {
    titulo: 'O link do fornecedor',
    conteudo: (
      <p>
        Cada fornecedor recebe um <strong>link exclusivo</strong> pelo WhatsApp, e <strong>não há senha</strong>:
        quem abre o link já vê a cotação. Por isso o link não deve ser repassado — se vazar, use{' '}
        <strong>Gerar novo link</strong>, que invalida o anterior. O link mostra a <strong>foto do veículo</strong>{' '}
        e as fotos enviadas para o fornecedor. Antes de enviar, ele revisa a proposta numa tela de conferência.
        Um fornecedor nunca vê os preços de outro.
      </p>
    ),
  },
  {
    titulo: 'Reenviar convite × Gerar novo link',
    conteudo: (
      <p>
        <strong>Reenviar convite</strong> dispara a mensagem de novo. <strong>Gerar novo link</strong> invalida o
        anterior e cria outro — use quando o link tiver vazado ou o fornecedor estiver bloqueado por erro de
        verificação. Em ambos os casos o fornecedor precisa usar o link mais recente que recebeu.
      </p>
    ),
  },
  {
    titulo: 'Estados da cotação',
    conteudo: (
      <p>
        <strong>Rascunho</strong> ainda não enviou convites e pode ser editada. <strong>Enviada</strong> e{' '}
        <strong>Em resposta</strong> estão recebendo propostas, inclusive depois do prazo: o prazo só gera avisos,
        e quem encerra o recebimento é você, em <strong>Encerrar respostas e iniciar negociação</strong>. O prazo é
        uma data: vale até o fim daquele dia. <strong>Em decisão</strong>{' '}
        encerrou o recebimento e libera a comparação. <strong>Aguardando aprovação</strong> tem item acima do menor preço parado com o
        gestor. <strong>Concluída</strong> já gerou os pedidos no ERP.
      </p>
    ),
  },
  {
    titulo: 'O sino de avisos',
    conteudo: (
      <div>
        <p>
          O sino ao lado da busca mostra o que aconteceu nas suas cotações enquanto você não estava olhando:{' '}
          <strong>proposta recebida</strong> ou fornecedor que informou que não vai cotar,{' '}
          <strong>prazo de respostas vencido</strong> (a cotação segue aberta até você encerrar),{' '}
          <strong>veredito do gestor</strong> (item aprovado ou
          reprovado, com o motivo), <strong>pedidos gerados</strong> ou com falha no ERP,{' '}
          <strong>link bloqueado</strong> por erro de verificação, <strong>fornecedor que tentou usar um link
          vencido</strong>, <strong>prazo chegando</strong> (3, 2 e 1 dia antes) com fornecedor sem responder e o resumo das{' '}
          <strong>entregas do dia</strong>. Quem aprova recebe também o aviso de{' '}
          <strong>item esperando aprovação</strong>.
        </p>
        <p>
          Há ainda o aviso de <strong>cotação parada</strong>: uma vez por dia o sistema confere há quanto tempo
          cada cotação está na etapa em que se encontra e avisa quando passa do esperado, dizendo o que falta
          para ela andar. Quando quem está devendo a ação é o gestor, ele também é avisado. Os prazos de cada
          etapa são configurados em <strong>Administração do Sistema &rarr; Cotações &rarr; Alertas</strong>, e
          etapa sem prazo definido não gera aviso nenhum.
        </p>
        <p>
          O número vermelho conta os <strong>não lidos</strong>. Clicar em um aviso marca como lido e abre a
          cotação correspondente; <strong>Limpar tudo</strong> marca todos de uma vez. Os avisos lidos{' '}
          <strong>continuam na lista</strong>, sem destaque — nada é perdido, e o histórico completo segue na
          cotação e na Auditoria. Quando houver mais avisos do que cabem na primeira carga, use{' '}
          <strong>Carregar mais</strong>.
        </p>
        <p>
          Os avisos chegam <strong>na hora</strong>, sem recarregar a página, e a cada 30 segundos a tela
          confere de novo, como reserva — se a conexão cair, você continua recebendo, só com um pouco de
          atraso. Se não for possível carregar os avisos, a tela avisa: sino sem número por causa de erro
          nunca é apresentado como "não há avisos".
        </p>
      </div>
    ),
  },
];

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatarDataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

// ── Wizard de nova cotação ────────────────────────────────────────────────────

/** Quantos fornecedores a busca do passo de convite traz por vez (at-16). */
const TAMANHO_DA_BUSCA_DE_FORNECEDOR = 50;

/** O convite sai só pelo WhatsApp (at-17): fornecedor sem telefone não pode ser convidado. */
const temWhatsapp = (f: Fornecedor) => f.contatos.some((c) => Boolean(c.telefone));

/** "AAAA-MM-DD" do campo de data, lido como dia (sem fuso). */
function formatarDia(aaaammdd: string): string {
  const [a, m, d] = aaaammdd.split('-');
  return `${d}/${m}/${a}`;
}

interface WizardProps {
  onFechar: () => void;
  onCriada: (id: string) => void;
}

const WizardNovaCotacao: React.FC<WizardProps> = ({ onFechar, onCriada }) => {
  const [passo, setPasso] = useState<1 | 2 | 3 | 4>(1);
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');

  /** Cotações já existentes — base do selo "Já cotada" e dos avisos de cadeia (12/08/2026). */
  const [cotacoesExistentes, setCotacoesExistentes] = useState<CotacaoListItem[]>([]);

  /*
   * A requisição escolhida — UMA (at-11, 28/09/2026; decisão do Victor: *"o correto é somente
   * iniciar um processo por requisição, sem mesclar processos"*).
   *
   * Entre 13/08 e 28/09/2026 isto era uma lista: o wizard deixava juntar inicial +
   * complementos do mesmo orçamento numa cotação só (item 21). Quem precisar cotar duas
   * versões cria duas cotações, cada uma com o número do seu processo no Nasajon.
   */
  const [selecionada, setSelecionada] = useState<Solicitacao | null>(null);

  /*
   * Itens JÁ COTADOS por peça, das cotações da cadeia (13/08/2026 — item 22). `null` = ainda
   * não consultado ou a consulta falhou; a distinção importa, porque mapa vazio significa
   * "conferi e não há repetição" e null significa "não consegui conferir" — dizer a primeira
   * coisa quando a verdade é a segunda é o defeito que o at-60 proíbe.
   */
  const [itensJaCotados, setItensJaCotados] = useState<Map<string, CotacaoDoItem[]> | null>(null);
  const [falhouConferenciaItens, setFalhouConferenciaItens] = useState(false);
  const [presetId, setPresetId] = useState('');
  // O prazo é só a DATA (at-17, teste com Compras de 29/10/2026): vale até o fim daquele dia, e o
  // servidor faz a conta no fuso da oficina. O horário de meia em meia hora saiu.
  const [prazo, setPrazo] = useState('');
  const [cnpjsSelecionados, setCnpjsSelecionados] = useState<string[]>([]);
  /*
   * Falha da carga inicial (at-17): sem isto, a lista vazia por erro dizia "Nenhuma requisição
   * encontrada" — afirmação sobre a fila de Compras tirada de um erro de rede (at-60).
   */
  const [erroCarga, setErroCarga] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  /** Fornecedor cujo cadastro de contatos está aberto por cima do wizard (at-11). */
  const [fornecedorEmCadastro, setFornecedorEmCadastro] = useState<Fornecedor | null>(null);
  /**
   * Os itens de cada fornecedor (at-18), pelas POSIÇÕES na lista de itens. CNPJ ausente = todos
   * (decisão do Victor, 30/10/2026: o fornecedor marcado nasce com todos e o comprador desmarca).
   */
  const [itensPorFornecedor, setItensPorFornecedor] = useState<Record<string, number[]>>({});
  /** O fornecedor cuja escolha de itens está aberta por cima do wizard. */
  const [escolhendoItensDe, setEscolhendoItensDe] = useState<Fornecedor | null>(null);

  /*
   * Busca de fornecedor NO SERVIDOR (portal-cotacoes/at-16, reunião de 28/10/2026: "difícil achar o
   * fornecedor no meio de tantos"). Até aqui o passo carregava os 100 primeiros em ordem alfabética
   * e filtrava na tela, então quem estava depois deles não aparecia nunca.
   *
   * `conhecidos` guarda todo fornecedor já visto: os SELECIONADOS continuam na tela (e no resumo)
   * quando a busca muda — a seleção não pode depender da página que está carregada.
   */
  const [buscaFornecedor, setBuscaFornecedor] = useState('');
  const [buscandoFornecedor, setBuscandoFornecedor] = useState(false);
  const [erroBuscaFornecedor, setErroBuscaFornecedor] = useState(false);
  const [conhecidos, setConhecidos] = useState<Record<string, Fornecedor>>({});
  const lembrarFornecedores = useCallback((lista: Fornecedor[]) => {
    setConhecidos((atual) => {
      const novo = { ...atual };
      lista.forEach((f) => {
        novo[f.cnpj] = f;
      });
      return novo;
    });
  }, []);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const [sols, forns, prs, cots] = await Promise.all([
          fetchSolicitacoesAprovadas(),
          fetchFornecedores({ pageSize: TAMANHO_DA_BUSCA_DE_FORNECEDOR }),
          fetchPresets(),
          /*
           * Cotações existentes: é com elas que o wizard sabe o que já foi cotado —
           * selo "Já cotada" no card e aviso de item repetido na cadeia (12/08/2026).
           * Uma página grande basta: o volume de cotações abertas é baixo por natureza,
           * e nenhuma rota nova foi criada para isso.
           */
          fetchCotacoes({ pageSize: 200 }),
        ]);
        if (!ativo) return;
        setSolicitacoes(sols.data);
        setFornecedores(forns.data);
        lembrarFornecedores(forns.data);
        setPresets(prs);
        setCotacoesExistentes(cots.data);
        const normal = prs.find((p) => p.nome === 'Normal') ?? prs[0];
        if (normal) setPresetId(normal.id);
        setAtualizadoEm(new Date());
      } catch (error) {
        if (ativo) setErroCarga(true);
        notify.errorFrom(error, 'Não foi possível carregar as requisições. Recarregue a página; se persistir, acione a TI.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
    // `lembrarFornecedores` é estável (useCallback sem dependência): a carga roda uma vez.
  }, [lembrarFornecedores]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return solicitacoes;
    return solicitacoes.filter(
      (s) =>
        s.numero.toLowerCase().includes(termo) ||
        (s.placa ?? '').toLowerCase().includes(termo) ||
        (s.veiculo ?? '').toLowerCase().includes(termo) ||
        (s.chassi ?? '').toLowerCase().includes(termo),
    );
  }, [solicitacoes, busca]);

  /*
   * Outras versões da MESMA cadeia que já foram cotadas (12/08/2026) — devolve os números
   * das cotações. O caso real: o complemento repete uma peça que a inicial já mandou cotar.
   *
   * ⚠️ Aviso no nível da CADEIA, não do item: a listagem de cotações não traz os itens (só
   * `totalItens`), e cruzar por `codigoProduto` exigiria uma requisição por cotação. Dizer
   * "esta cadeia já tem cotação" é verdade verificada; apontar QUAL item repetiu exigiria
   * um dado que a tela não tem — o cruzamento fino está no backlog do Portal.
   */
  /** As cotações da cadeia que não são da requisição escolhida agora. */
  const cotacoesDaCadeiaJaFeitas = useMemo(() => {
    if (!selecionada) return [] as CotacaoListItem[];
    return cotacoesDaCadeia(selecionada.numero, cotacoesExistentes).filter(
      (c) => c.solicitacaoNumero !== selecionada.numero,
    );
  }, [selecionada, cotacoesExistentes]);

  /** Versões da cadeia aprovadas e nunca cotadas — exceto a escolhida agora. */
  const versoesPendentes = useMemo(() => {
    if (!selecionada) return [] as string[];
    return versoesNaoCotadasDaCadeia(
      selecionada.numero,
      solicitacoes.map((s) => s.numero),
      cotacoesExistentes,
    );
  }, [selecionada, solicitacoes, cotacoesExistentes]);

  /** Os itens da requisição escolhida — é o que vai para a cotação e para o fornecedor. */
  const itensDaSolicitacao = useMemo(() => selecionada?.itens ?? [], [selecionada]);
  // Outra requisição, outros itens: as escolhas por posição deixam de valer (at-18).
  useEffect(() => setItensPorFornecedor({}), [selecionada]);

  /*
   * Colunas da tabela de itens do passo 2 (portal-cotacoes/at-14, pedido do Victor de 28/10/2026):
   * no `TabelaSistema`, com uma coluna por referência de preço — os três pares rótulo + valor numa
   * célula só eram cortados no modal. Memorizadas porque o `TabelaSistema` mede as colunas a cada
   * troca do array: recriá-lo a cada render faria a medição rodar sem parar.
   */
  type ItemDaSolicitacao = (typeof itensDaSolicitacao)[number];
  const colunasDosItens = useMemo<ColunaTabela<ItemDaSolicitacao>[]>(() => {
    const jaCotadaEm = (item: ItemDaSolicitacao) => itensJaCotados?.get(item.codigoProduto);
    return [
      {
        titulo: 'Código',
        largura: 110,
        texto: (i) => i.codigoProduto,
        render: (i) => <span className="pc-mono">{i.codigoProduto}</span>,
      },
      {
        titulo: 'Descrição',
        largura: 200,
        larguraMaxima: 280,
        // Em quais cotações da cadeia esta peça JÁ está (item 22). `undefined` com o mapa
        // carregado = conferido e não repete.
        texto: (i) => {
          const ja = jaCotadaEm(i);
          return ja ? `${i.descricao} — já cotada em ${ja.map((c) => c.numero).join(', ')}` : i.descricao;
        },
        render: (i) => {
          const ja = jaCotadaEm(i);
          return (
            <>
              {i.descricao}
              {ja && <span className="pc-item-ja-cotado">já em {ja.map((c) => c.numero).join(', ')}</span>}
            </>
          );
        },
      },
      // Qtd e unidade numa coluna só ("1 UN"): duas colunas estreitas custavam a largura que as
      // referências precisam. O selo "somada" saiu na at-11; a quantidade é a da requisição.
      { titulo: 'Qtd', largura: 60, texto: (i) => `${Number(i.quantidade)} ${i.unidade}` },
      ...colunasDeReferencias<ItemDaSolicitacao>(),
    ];
  }, [itensJaCotados]);

  /**
   * Cotações da cadeia AINDA EM ANDAMENTO: é o recorte do aviso sobre complemento que remove
   * ou reduz peça (item 20). Concluída não entra — ali o pedido já existe e quem resolve é o
   * ERP, não o Portal.
   */
  const cotacoesEmAndamentoNaCadeia = useMemo(
    () => cotacoesDaCadeiaJaFeitas.filter(estaEmAndamento),
    [cotacoesDaCadeiaJaFeitas],
  );

  /*
   * Conferência POR PEÇA (13/08/2026 — item 22): busca os itens das cotações da cadeia para
   * dizer qual peça já está cotada, e onde. Roda ao entrar no passo 2, que é quando os itens
   * aparecem — carregar antes gastaria requisição para quem só está escolhendo a solicitação.
   */
  useEffect(() => {
    if (passo !== 2 || cotacoesDaCadeiaJaFeitas.length === 0) {
      setItensJaCotados(null);
      setFalhouConferenciaItens(false);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const { data } = await fetchItensEmLote(cotacoesDaCadeiaJaFeitas.map((c) => c.id));
        if (!ativo) return;
        setItensJaCotados(itensJaCotadosNaCadeia(data));
        setFalhouConferenciaItens(false);
      } catch {
        if (!ativo) return;
        /*
         * Falhou: o mapa fica NULO e a tela diz que não conseguiu conferir. Deixar o mapa
         * vazio faria a ausência de badge parecer "conferi, nenhuma peça repetida" — erro de
         * API virando afirmação de negócio, que é o que a regra do at-60 proíbe. O aviso
         * agregado de cadeia continua aparecendo: ele não depende desta consulta.
         */
        setItensJaCotados(null);
        setFalhouConferenciaItens(true);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [passo, cotacoesDaCadeiaJaFeitas]);

  /*
   * A busca roda no servidor, com pausa de 300 ms entre teclas. Falha não vira "nenhum fornecedor"
   * (at-60): a tela diz que não conseguiu buscar e mantém o que já estava.
   */
  const primeiraBusca = useRef(true);
  useEffect(() => {
    if (primeiraBusca.current) {
      primeiraBusca.current = false;
      return;
    }
    let ativo = true;
    const espera = setTimeout(async () => {
      setBuscandoFornecedor(true);
      try {
        const resultado = await fetchFornecedores({
          search: buscaFornecedor.trim() || undefined,
          pageSize: TAMANHO_DA_BUSCA_DE_FORNECEDOR,
        });
        if (!ativo) return;
        setFornecedores(resultado.data);
        lembrarFornecedores(resultado.data);
        setErroBuscaFornecedor(false);
      } catch {
        if (ativo) setErroBuscaFornecedor(true);
      } finally {
        if (ativo) setBuscandoFornecedor(false);
      }
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [buscaFornecedor, lembrarFornecedores]);

  /** Selecionados que a busca atual não trouxe vêm primeiro, para a seleção nunca sumir. */
  const fornecedoresNaTela = useMemo(() => {
    const naBusca = new Set(fornecedores.map((f) => f.cnpj));
    const foraDaBusca = cnpjsSelecionados.filter((c) => !naBusca.has(c) && conhecidos[c]).map((c) => conhecidos[c]);
    return [...foraDaBusca, ...fornecedores];
  }, [fornecedores, cnpjsSelecionados, conhecidos]);

  /** Os itens do wizard no formato da escolha de itens: a posição vira o id (at-18). */
  const itensEscolhiveis = itensDaSolicitacao.map((i, idx) => ({
    id: String(idx),
    descricao: i.descricao,
    detalhe: `${Number(i.quantidade)} ${i.unidade}`,
  }));
  const itensDoFornecedor = (cnpj: string): number[] =>
    itensPorFornecedor[cnpj] ?? itensDaSolicitacao.map((_, idx) => idx);
  /**
   * Itens que nenhum fornecedor marcado recebe (at-18): ninguém os cotaria. O servidor também recusa
   * (`ITEM_SEM_FORNECEDOR`); aqui a tela diz antes de o comprador chegar ao último passo.
   */
  const itensSemFornecedor =
    cnpjsSelecionados.length === 0
      ? []
      : itensDaSolicitacao.filter((_, idx) => !cnpjsSelecionados.some((cnpj) => itensDoFornecedor(cnpj).includes(idx)));

  /** Selecionados que perderam o WhatsApp (o contato pode ter sido apagado depois de marcar). */
  const semWhatsapp = cnpjsSelecionados.filter((cnpj) => {
    const f = conhecidos[cnpj];
    return !f || !temWhatsapp(f);
  });

  /**
   * Clique no card de requisição (at-11, 28/09/2026): seleção **única**. Clicar em outro card troca
   * a escolha; clicar no já escolhido o desmarca.
   */
  function alternarSolicitacao(s: Solicitacao) {
    setSelecionada((atual) => (atual?.numero === s.numero ? null : s));
  }

  function alternarFornecedor(f: Fornecedor) {
    const marcado = cnpjsSelecionados.includes(f.cnpj);
    // Sem WhatsApp, marcar não é possível; desmarcar continua (o contato pode ter sumido depois).
    if (!marcado && !temWhatsapp(f)) return;
    setCnpjsSelecionados((atual) =>
      atual.includes(f.cnpj) ? atual.filter((c) => c !== f.cnpj) : [...atual, f.cnpj],
    );
  }

  /** Pergunta ao Nasajon na hora pelas requisições aprovadas (at-17) e recarrega a lista. */
  async function atualizarLista() {
    setAtualizando(true);
    try {
      await atualizarRequisicoes();
    } catch (error) {
      // 409/429 trazem o motivo do servidor; a lista que está na tela continua valendo.
      notify.warning((error as Error).message || 'Não foi possível atualizar as requisições agora.', {
        toastId: 'pc-atualizar-requisicoes',
      });
      setAtualizando(false);
      return;
    }
    try {
      const [sols, cots] = await Promise.all([fetchSolicitacoesAprovadas(), fetchCotacoes({ pageSize: 200 })]);
      setSolicitacoes(sols.data);
      setCotacoesExistentes(cots.data);
      setErroCarga(false);
      setAtualizadoEm(new Date());
    } catch {
      notify.warning('A atualização foi feita, mas a lista não recarregou. Recarregue a página.', {
        toastId: 'pc-atualizar-requisicoes',
      });
    } finally {
      setAtualizando(false);
    }
  }

  /**
   * Recarrega o que a criação pode ter tornado obsoleto (at-11).
   *
   * As duas listas juntas, e não só as requisições: a recusa pode ser
   * `SOLICITACAO_NAO_APROVADA` (a requisição saiu das aprovadas) **ou**
   * `REQUISICAO_JA_EM_COTACAO` (alguém a cotou antes), e o segundo caso só some da tela
   * quando as cotações existentes chegam de novo — é delas que sai o selo "Já cotada".
   *
   * Falha silenciosa aqui é deliberada: quem chama já mostrou o erro real da criação, e um
   * segundo toast sobre a recarga empurraria aquele para fora da tela.
   */
  async function carregarSolicitacoes() {
    try {
      const [sols, cots] = await Promise.all([
        fetchSolicitacoesAprovadas(),
        fetchCotacoes({ pageSize: 200 }),
      ]);
      setSolicitacoes(sols.data);
      setCotacoesExistentes(cots.data);
      // A requisição escolhida pode não estar mais na lista: manter a seleção deixaria o
      // usuário tentando de novo exatamente o que o servidor acabou de recusar.
      setSelecionada((atual) => sols.data.find((s) => s.numero === atual?.numero) ?? null);
    } catch {
      // Ver o cabeçalho: o erro que importa é o da criação, já exibido por quem chamou.
    }
  }

  /**
   * Recarrega os fornecedores depois de o comprador cadastrar um contato no wizard (at-11). É o
   * que habilita o card do fornecedor que acabou de ganhar WhatsApp.
   */
  async function recarregarFornecedores(cnpjTocado: string) {
    try {
      // Busca o próprio fornecedor tocado: ele pode não estar na página de busca atual.
      const forns = await fetchFornecedores({ search: cnpjTocado, pageSize: TAMANHO_DA_BUSCA_DE_FORNECEDOR });
      lembrarFornecedores(forns.data);
      setFornecedores((atual) => atual.map((f) => forns.data.find((n) => n.cnpj === f.cnpj) ?? f));
    } catch (error) {
      // O cadastro em si deu certo (quem chama só chega aqui depois do 201): o que falhou foi
      // a recarga. Dizer isso é melhor que um erro genérico, que faria o comprador cadastrar
      // o contato de novo achando que se perdeu.
      notify.warning(
        'O contato foi cadastrado, mas a lista de fornecedores não atualizou. Reabra o passo de fornecedores.',
        { toastId: 'pc-recarga-fornecedores' },
      );
    }
  }

  async function salvar() {
    if (!selecionada) return;
    setSalvando(true);
    try {
      const cotacao = await criarCotacao({
        solicitacaoNumero: selecionada.numero,
        placa: selecionada.placa ?? undefined,
        veiculo: selecionada.veiculo ?? undefined,
        presetId: presetId || undefined,
        // Só a data: o servidor fecha o prazo no fim do dia, no fuso da oficina (at-17).
        prazoRespostaAte: prazo,
        itens: itensDaSolicitacao,
        // `itens` só para quem não recebe todos (at-18): ausente, o servidor manda todos.
        fornecedores: cnpjsSelecionados.map((cnpj) =>
          itensPorFornecedor[cnpj] && itensPorFornecedor[cnpj].length < itensDaSolicitacao.length
            ? { cnpj, itens: itensPorFornecedor[cnpj] }
            : { cnpj },
        ),
      });
      toast.success('Rascunho criado. Revise e envie os convites quando quiser.');
      onCriada(cotacao.id);
    } catch (error) {
      toast.error((error as Error).message);
      /*
       * A criação REVALIDA a aprovação na origem (at-11): a recusa costuma significar que a
       * requisição mudou de estado no Nasajon depois de esta lista carregar — reprovada,
       * cancelada, ou já cotada por outra pessoa. Recarregar a lista é o que faz a tela parar
       * de oferecer o que o servidor não aceita mais; sem isso o comprador tentaria de novo
       * no mesmo card e tomaria o mesmo erro.
       */
      await carregarSolicitacoes();
    } finally {
      setSalvando(false);
      setConfirmando(false);
    }
  }

  const podeAvancar =
    // Solicitação SEM ITEM não inicia cotação (pedido do Victor, 12/08/2026): não há o que
    // cotar, e o backend recusa no fim (`@ArrayMinSize(1)`) — deixar avançar só levaria o
    // usuário a descobrir isso no último passo, depois de escolher preset, prazo e
    // fornecedores. A causa (Cilia criando solicitação vazia) foi corrigida no BUG-002, mas
    // esta guarda é do Portal: o provider do ERP real também pode devolver algo sem item.
    (passo === 1 && selecionada != null && itensDaSolicitacao.length > 0) ||
    passo === 2 ||
    (passo === 3 &&
      Boolean(prazo) &&
      cnpjsSelecionados.length > 0 &&
      semWhatsapp.length === 0 &&
      itensSemFornecedor.length === 0);

  // Botão bloqueado sempre diz o motivo: sem isso, o comprador não sabe o que falta.
  const motivoBloqueio =
    passo === 3 && !podeAvancar
      ? !prazo && cnpjsSelecionados.length === 0
        ? 'Escolha o prazo e ao menos um fornecedor.'
        : !prazo
          ? 'Escolha o prazo para respostas.'
          : cnpjsSelecionados.length === 0
            ? 'Escolha ao menos um fornecedor.'
            : semWhatsapp.length > 0
              ? 'Há fornecedor marcado sem WhatsApp.'
              : 'Há item que nenhum fornecedor vai receber.'
      : null;

  const presetEscolhido = presets.find((p) => p.id === presetId);

  return (
    <div className="pc-drawer-overlay" onClick={onFechar}>
      <div className="pc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="pc-drawer-header">
          <div>
            <h2 className="pc-card-titulo" style={{ margin: 0 }}>Nova Cotação</h2>
            <p className="pc-texto-secundario" style={{ margin: '4px 0 0' }}>
              Passo {passo} de 4
            </p>
          </div>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="pc-passos" style={{ display: 'flex', padding: '16px 20px', alignItems: 'center', width: '100%', boxSizing: 'border-box', borderBottom: '1px solid var(--tema-linha, #e4e8ef)', gap: 4 }}>
          {['Requisição', 'Itens', 'Parâmetros', 'Revisão'].map((rotulo, i) => {
            const n = i + 1;
            const ativo = n <= passo;
            return (
              <React.Fragment key={rotulo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: ativo ? '#EA5D0E' : 'var(--tema-fundo, #E8EDF5)',
                      color: ativo ? '#fff' : '#6B7A99',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {n < passo ? '✓' : n}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: ativo ? 600 : 500, color: ativo ? 'var(--tema-texto, #1A1C1B)' : '#6B7A99', whiteSpace: 'nowrap' }}>{rotulo}</span>
                </div>
                {n < 4 && (
                  <div style={{ flex: 1, height: 1, backgroundColor: 'var(--tema-fundo-2, #E4E8EF)', margin: '0 8px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="pc-drawer-corpo">
          {carregando ? (
            <div className="pc-carregando">Carregando...</div>
          ) : (
            <>
              {passo === 1 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span className="pc-label" style={{ margin: 0 }}>
                      Escolha a requisição de compra aprovada
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                    <input
                      className="pc-input"
                      placeholder="Buscar por número, placa, veículo ou chassi"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      style={{ flex: 1, minWidth: 220 }}
                    />
                    <button
                      type="button"
                      className="pc-btn pc-btn--secundario pc-btn--sm"
                      disabled={atualizando}
                      onClick={atualizarLista}
                    >
                      {atualizando ? 'Atualizando...' : 'Atualizar'}
                    </button>
                    {atualizadoEm && (
                      <span className="pc-texto-secundario">
                        Atualizado às{' '}
                        {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <p className="pc-hint" style={{ marginBottom: 12 }}>
                    Cada requisição vira uma cotação, com o número do processo no Nasajon. Aprovou agora e ela não
                    está na lista? Use Atualizar.
                  </p>
                  {erroCarga ? (
                    <p className="erro-de-tela">
                      Não foi possível carregar as requisições aprovadas. Pode haver requisições que não estão
                      aparecendo aqui. Use Atualizar; se persistir, acione a TI.
                    </p>
                  ) : filtradas.length === 0 ? (
                    <p className="pc-hint">Nenhuma requisição encontrada.</p>
                  ) : (
                    <div className="pc-solicitacoes-grid">
                      {filtradas.map((s) => {
                        /*
                          Solicitação sem item não é selecionável (pedido do Victor,
                          12/08/2026): não existe cotação de nada. Acontece com versão de
                          orçamento que não pediu peça nova — o Cilia deixou de criar
                          solicitação assim (BUG-002), mas as antigas seguem no banco e o
                          provider do ERP real não está sob nosso controle.
                        */
                        const semItens = s.itens.length === 0;
                        const estaSelecionada = selecionada?.numero === s.numero;
                        // Com algo já escolhido, clicar em outro card TROCA a escolha — a
                        // cotação atende uma requisição só (at-11). O título avisa antes,
                        // para o clique não parecer perda.
                        const trocaria = selecionada != null && !estaSelecionada;
                        return (
                        <button
                          key={s.numero}
                          type="button"
                          disabled={semItens}
                          title={
                            semItens
                              ? 'Esta requisição não tem item para cotar'
                              : trocaria
                                ? 'Cada cotação atende uma requisição — selecionar esta substitui a escolha atual'
                                : undefined
                          }
                          className={`pc-solicitacao-card ${estaSelecionada ? 'pc-solicitacao-card--selecionada' : ''} ${semItens ? 'pc-solicitacao-card--vazia' : ''}`}
                          onClick={() => !semItens && alternarSolicitacao(s)}
                        >
                          <div className="pc-solicitacao-card-header">
                            {/*
                              O nome traz a origem junto: "9100-v4 - 3º Complemento" (pedido
                              do Victor, 12/08/2026). É o que liga o card à linha da tela de
                              Orçamentos, onde a coluna "Complemento" mostra o mesmo ordinal.
                              Some quando a origem não vem — o provider do ERP real não
                              conhece complemento.
                            */}
                            <span className="pc-solicitacao-card-num">
                              {s.origem ? `${s.numero} - ${s.origem}` : s.numero}
                            </span>
                            {/*
                              Selo "Já cotada" (decisão do Victor, 12/08/2026): a solicitação
                              CONTINUA selecionável — recotar de propósito é legítimo (item
                              sem vencedor, prazo estourado). O selo evita a duplicata por
                              distração, que é o caso comum.
                            */}
                            {cotacoesDaSolicitacao(s.numero, cotacoesExistentes).length > 0 && (
                              <span className="pc-solicitacao-card-cotada">Já cotada</span>
                            )}
                            {s.placa && (
                              <span className="pc-solicitacao-card-placa">{s.placa}</span>
                            )}
                          </div>
                          <div className="pc-solicitacao-card-veiculo">
                            {s.veiculo ?? 'Veículo não informado'}
                          </div>
                          <div className="pc-solicitacao-card-itens">
                            {semItens ? (
                              /* O motivo, não a contagem: "0 itens:" seguido de nada não
                                 explica por que o card está inerte. */
                              <span className="pc-solicitacao-card-motivo">
                                Nenhum item para cotar — esta versão do orçamento não pediu peça nova.
                              </span>
                            ) : (
                              <>
                                {s.itens.length} {s.itens.length === 1 ? 'item' : 'itens'}:{' '}
                                {s.itens.map((i) => i.descricao).join(', ')}
                              </>
                            )}
                          </div>
                          {/*
                            Quando aprovar em: dois complementos do MESMO orçamento têm
                            veículo e placa iguais e, porque complemento é foto cumulativa
                            do inicial, costumam repetir a lista de itens. Sem a data (e o
                            número, que passou a trazer a versão do orçamento em
                            12/08/2026), os cards ficam indistinguíveis na tela.
                          */}
                          <div className="pc-solicitacao-card-aprovada">
                            Aprovada em {new Date(s.aprovadaEm).toLocaleString('pt-BR')}
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {passo === 2 && selecionada && (
                <>
                  <p className="pc-label">Itens da requisição {selecionada.numero}</p>
                  <p className="pc-hint" style={{ marginBottom: 12 }}>
                    {selecionada.veiculo} · {selecionada.placa} · Chassi {selecionada.chassi ?? 'não informado'}
                  </p>

                  {/*
                    Avisos de CADEIA (12/08/2026): todas as versões do mesmo orçamento
                    (`SOL-9100-v1`, `-v3`, `-v4`) são a mesma cadeia. Dois riscos reais que
                    a tela passou a dizer em voz alta.
                  */}
                  {cotacoesDaCadeiaJaFeitas.length > 0 && (
                    <p className="pc-aviso pc-aviso--atencao">
                      Este orçamento já tem cotação em outra versão:{' '}
                      <strong>{cotacoesDaCadeiaJaFeitas.map((c) => c.numero).join(', ')}</strong>.{' '}
                      {itensJaCotados
                        ? 'As peças que já estão nessas cotações aparecem marcadas na tabela abaixo.'
                        : 'Confira se as peças desta requisição não estão repetidas antes de seguir — complemento costuma trazer só o que faltou, mas a conferência é sua.'}
                    </p>
                  )}
                  {/*
                    A consulta por peça falhou (13/08/2026): a tela diz que NÃO CONSEGUIU
                    conferir. Ficar calada aqui faria a ausência de marcação na tabela
                    parecer "nenhuma peça repetida" — erro de API virando conclusão de
                    negócio, o defeito que a regra do at-60 existe para impedir.
                  */}
                  {falhouConferenciaItens && (
                    <p className="pc-aviso pc-aviso--atencao">
                      Não foi possível conferir quais peças já estão nas outras cotações desta cadeia. A
                      tabela abaixo pode conter peças repetidas sem marcação — confira nas cotações citadas
                      antes de seguir.
                    </p>
                  )}
                  {/*
                    Item 20 do backlog: o complemento pode ter REMOVIDO ou REDUZIDO peça que a
                    cotação em andamento ainda está cotando. O Portal não tem como detectar
                    isso sozinho — a solicitação de complemento carrega só o acréscimo, e a
                    remoção só existe no diff do orçamento. O aviso detalhado está na
                    conferência do Cilia; aqui é reforço para quem chegou pelo Portal.
                  */}
                  {cotacoesEmAndamentoNaCadeia.length > 0 && (
                    <p className="pc-aviso pc-aviso--info">
                      {cotacoesEmAndamentoNaCadeia.length === 1 ? 'A cotação' : 'As cotações'}{' '}
                      <strong>{cotacoesEmAndamentoNaCadeia.map((c) => c.numero).join(', ')}</strong>{' '}
                      {cotacoesEmAndamentoNaCadeia.length === 1 ? 'ainda está em andamento' : 'ainda estão em andamento'}
                      . Se este complemento tirou peças do orçamento ou baixou quantidades, confira antes de
                      fechar a cesta lá — a conferência do orçamento mostra o que saiu.
                    </p>
                  )}
                  {versoesPendentes.length > 0 && (
                    <div className="pc-aviso pc-aviso--critico pc-aviso--com-icone">
                      <IconeAlerta />
                      {/*
                        ⚠️ O texto inteiro vive num ÚNICO filho. Com `--com-icone` o aviso é
                        flex, e texto solto + `<strong>` viraria um item flex por trecho —
                        a frase quebraria em colunas (defeito visto em tela em 13/08/2026).
                      */}
                      <span>
                        Nesta cadeia, {versoesPendentes.length === 1 ? 'a versão' : 'as versões'}{' '}
                        <strong>{versoesPendentes.join(', ')}</strong>{' '}
                        {versoesPendentes.length === 1 ? 'está aprovada e nunca foi cotada' : 'estão aprovadas e nunca foram cotadas'}
                        . Volte ao passo anterior para juntá-{versoesPendentes.length === 1 ? 'la' : 'las'} nesta
                        cotação, ou crie a cotação {versoesPendentes.length === 1 ? 'dela' : 'delas'} depois desta.
                      </span>
                    </div>
                  )}

                  <TabelaSistema
                    colunas={colunasDosItens}
                    itens={itensDaSolicitacao}
                    chave={(i) => `${i.codigoProduto}-${i.descricao}`}
                    vazio="Esta requisição não tem itens."
                  />
                  {/*
                    Contexto por item (quantidade adicional de complemento): fora da tabela,
                    porque é frase e não caberia em célula de uma linha — e o fornecedor
                    também vai ler isso no formulário dele.
                  */}
                  {itensDaSolicitacao.some((i) => i.observacao) && (
                    <div className="pc-aviso pc-aviso--info" style={{ marginTop: 12 }}>
                      {itensDaSolicitacao
                        .filter((i) => i.observacao)
                        .map((i) => (
                          <p key={i.codigoProduto} style={{ margin: 0 }}>
                            <strong>{i.descricao}:</strong> {i.observacao}
                          </p>
                        ))}
                    </div>
                  )}
                  <p className="pc-hint" style={{ marginTop: 12 }}>
                    Os itens vêm da requisição aprovada e não são editados aqui. As referências de preço são
                    internas — o fornecedor não as vê. Passe o mouse sobre um valor para ver o detalhe (quantas
                    compras formam a média, quando o preço online foi pesquisado); <strong>*</strong> no preço online
                    indica que o anúncio não cita tudo e a compatibilidade precisa ser conferida.
                  </p>
                </>
              )}

              {passo === 3 && (
                <>
                  <div className="pc-grid-2">
                    <div className="pc-campo">
                      <label className="pc-label" htmlFor="pc-preset">
                        Preset de pesos
                      </label>
                      <select
                        id="pc-preset"
                        className="pc-select"
                        value={presetId}
                        onChange={(e) => setPresetId(e.target.value)}
                      >
                        {presets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nome} (preço {p.pesoPreco}% · prazo {p.pesoPrazo}%)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="pc-campo">
                      <label className="pc-label" htmlFor="pc-prazo">
                        Prazo para respostas
                      </label>
                      <input
                        id="pc-prazo"
                        type="date"
                        className="pc-input"
                        value={prazo}
                        onChange={(e) => setPrazo(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="pc-hint" style={{ marginBottom: 14 }}>
                    O preset define o peso de preço e prazo no score de apoio. Ele fica congelado nesta cotação: se
                    alguém editar o preset depois, esta cotação não muda. O prazo vale até o fim do dia escolhido.
                  </p>

                  <span className="pc-label" style={{ marginBottom: 10 }}>Fornecedores convidados</span>
                  <input
                    className="pc-input"
                    aria-label="Buscar fornecedor"
                    placeholder="Buscar por razão social, CNPJ ou código do ERP"
                    value={buscaFornecedor}
                    onChange={(e) => setBuscaFornecedor(e.target.value)}
                    style={{ marginBottom: 6 }}
                  />
                  <p className="pc-hint" style={{ marginBottom: 10 }}>
                    {buscandoFornecedor
                      ? 'Buscando...'
                      : `Mostrando até ${TAMANHO_DA_BUSCA_DE_FORNECEDOR} fornecedores. Os selecionados continuam na lista quando a busca muda.`}
                  </p>
                  {erroBuscaFornecedor && (
                    <p className="erro-de-tela">
                      Não foi possível buscar fornecedores. Tente de novo; se persistir, acione a TI.
                    </p>
                  )}
                  {fornecedoresNaTela.length === 0 ? (
                    <p className="pc-hint">
                      {buscaFornecedor.trim()
                        ? `Nenhum fornecedor encontrado para "${buscaFornecedor.trim()}".`
                        : 'Nenhum fornecedor cadastrado. Importe do ERP em Administração do Sistema, módulo Cotações, antes de criar a cotação.'}
                    </p>
                  ) : (
                    <div className="pc-solicitacoes-grid">
                      {fornecedoresNaTela.map((f) => {
                        const selecionado = cnpjsSelecionados.includes(f.cnpj);
                        const contatos = f.contatos || [];
                        const semWhats = !temWhatsapp(f);
                        return (
                          <div
                            key={f.cnpj}
                            className="pc-solicitacao-card"
                            onClick={() => alternarFornecedor(f)}
                            title={semWhats && !selecionado ? 'Cadastre o WhatsApp do fornecedor para convidá-lo' : undefined}
                            aria-disabled={semWhats && !selecionado}
                            style={{
                              cursor: semWhats && !selecionado ? 'not-allowed' : 'pointer',
                              opacity: semWhats && !selecionado ? 0.65 : 1,
                              borderColor: selecionado ? '#ea5d0e' : 'var(--tema-linha-2, #d6dce6)',
                              background: selecionado ? 'var(--tema-fundo, #fff4ee)' : 'var(--tema-superficie, #ffffff)',
                              padding: 14,
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                cursor: 'pointer',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: 4,
                                  border: '2px solid',
                                  borderColor: selecionado ? '#ea5d0e' : 'var(--tema-linha-2, #d6dce6)',
                                  background: selecionado ? '#ea5d0e' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  flexShrink: 0,
                                }}
                              >
                                {selecionado && '✓'}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                  <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--tema-texto, #1a1c1b)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                    {f.razaoSocial}
                                  </p>
                                  {/*
                                    Sem emoji (regra do Victor, 29/07/2026): era "⚠️ Sem
                                    contato" até a at-11. O módulo já tem o ícone próprio.
                                  */}
                                  {semWhats && (
                                    <span style={{ color: '#d92d20', fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                                      <IconeAlerta tamanho={11} /> Sem WhatsApp
                                    </span>
                                  )}
                                </div>
                                <p style={{ margin: '2px 0 0', fontSize: 10, color: '#6b7a99' }}>
                                  {f.cnpj} · {contatos.length} contato{contatos.length === 1 ? '' : 's'}
                                </p>
                              </div>
                            </div>
                            {/*
                              Cadastrar contato SEM sair do wizard (at-11, pedido do Victor).
                              O cadastro de fornecedores do Nasajon não tem telefone nem
                              e-mail (0 de 400, medido em 24/08/2026), então o contato é
                              sempre digitado aqui — e mandar o comprador abandonar a cotação
                              para ir à Administração do Sistema era atrito garantido.
                              ⚠️ `stopPropagation`: o card inteiro é a área de seleção do
                              fornecedor, e sem isso abrir o cadastro marcaria/desmarcaria o
                              fornecedor junto.
                            */}
                            <button
                              type="button"
                              className="pc-btn pc-btn--secundario"
                              style={{ fontSize: 11, padding: '4px 10px', alignSelf: 'flex-start' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFornecedorEmCadastro(f);
                              }}
                            >
                              {semWhats ? 'Cadastre o WhatsApp' : 'Contatos'}
                            </button>
                            {/*
                              Quais itens este fornecedor recebe (at-18): fornecedor especializado não
                              responde orçamento inteiro. `stopPropagation` pelo mesmo motivo do botão acima.
                            */}
                            {selecionado && itensDaSolicitacao.length > 1 && (
                              <button
                                type="button"
                                className="pc-btn pc-btn--secundario"
                                style={{ fontSize: 11, padding: '4px 10px', alignSelf: 'flex-start' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEscolhendoItensDe(f);
                                }}
                              >
                                {itensDoFornecedor(f.cnpj).length === itensDaSolicitacao.length
                                  ? `Itens: todos (${itensDaSolicitacao.length})`
                                  : `Itens: ${itensDoFornecedor(f.cnpj).length} de ${itensDaSolicitacao.length}`}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {semWhatsapp.length > 0 && (
                    <div className="pc-aviso pc-aviso--atencao" style={{ marginTop: 14 }}>
                      {semWhatsapp.length} fornecedor{semWhatsapp.length > 1 ? 'es' : ''} marcado
                      {semWhatsapp.length > 1 ? 's' : ''} sem WhatsApp. O convite sai só pelo WhatsApp — cadastre o
                      número no card do fornecedor, ou desmarque-o.
                    </div>
                  )}
                  {itensSemFornecedor.length > 0 && (
                    <div className="pc-aviso pc-aviso--atencao" style={{ marginTop: 14 }}>
                      Nenhum fornecedor marcado vai receber: <strong>{itensSemFornecedor.map((i) => i.descricao).join('; ')}</strong>.
                      Marque esses itens em algum fornecedor, pelo botão de itens no card dele.
                    </div>
                  )}
                </>
              )}

              {passo === 4 && selecionada && (
                <>
                  <p className="pc-label">Revisão antes de criar o rascunho</p>
                  <div className="pc-resumo">
                    <p className="pc-resumo-rotulo">Requisição</p>
                    <p>
                      {selecionada.numero}
                      {selecionada.origem && (
                        <span className="pc-texto-secundario"> · {selecionada.origem}</span>
                      )}
                    </p>
                    <p className="pc-texto-secundario">
                      {selecionada.veiculo} · {selecionada.placa}
                    </p>
                    <p className="pc-texto-secundario">
                      Chassi: {selecionada.chassi ?? 'Não informado'} · Local de entrega:{' '}
                      {selecionada.localEntrega?.nome
                        ? `${selecionada.localEntrega.nome}${selecionada.localEntrega.endereco ? `, ${selecionada.localEntrega.endereco}` : ''}`
                        : 'Não informado'}
                    </p>
                    {/*
                      O número da cotação é o do processo no Nasajon (at-11). Dizer isto aqui,
                      antes de criar, é o que evita a pergunta "onde está a COT-2026-…?" — ela
                      não existe mais, e quem procurar o documento no ERP procura por este
                      número.
                    */}
                    <p className="pc-texto-secundario">
                      A cotação vai receber o número do processo: <strong>{selecionada.numero}</strong>
                    </p>
                  </div>
                  <div className="pc-resumo">
                    <p className="pc-resumo-rotulo">Itens ({itensDaSolicitacao.length})</p>
                    {itensDaSolicitacao.map((i) => (
                      <p key={i.codigoProduto}>
                        {i.descricao}{' '}
                        <span className="pc-texto-secundario">
                          &times; {i.quantidade} {i.unidade}
                        </span>
                      </p>
                    ))}
                  </div>
                  <div className="pc-resumo">
                    <p className="pc-resumo-rotulo">Preset e prazo</p>
                    <p>{presetEscolhido?.nome}</p>
                    {prazo && <p className="pc-texto-secundario">Respostas até {formatarDia(prazo)}</p>}
                  </div>
                  <div className="pc-resumo">
                    <p className="pc-resumo-rotulo">Fornecedores ({cnpjsSelecionados.length})</p>
                    {cnpjsSelecionados.map((cnpj) => (
                      <p key={cnpj}>
                        {conhecidos[cnpj]?.razaoSocial}{' '}
                        <span className="pc-texto-secundario">
                          (WhatsApp,{' '}
                          {itensDoFornecedor(cnpj).length === itensDaSolicitacao.length
                            ? 'todos os itens'
                            : `${itensDoFornecedor(cnpj).length} de ${itensDaSolicitacao.length} itens`}
                          )
                        </span>
                      </p>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="pc-drawer-rodape" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            className="pc-btn pc-btn--secundario"
            onClick={() => (passo === 1 ? onFechar() : setPasso((p) => (p - 1) as 1 | 2 | 3))}
            style={{ border: 'none', background: 'transparent', color: '#6B7A99', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            &larr; {passo === 1 ? 'Fechar' : 'Voltar'}
          </button>
          {/*
            Cadastro de contato do fornecedor SEM sair do wizard (at-11) — o mesmo modal que a
            aba Fornecedores da configuração abre, e não um formulário novo.

            🔴 Montado DENTRO do `.pc-drawer`, e isso é a guarda de "clicou fora": o wizard
            fecha por `onClick` no overlay, e só o conteúdo do drawer tem `stopPropagation`.
            Fora daqui, o primeiro clique dentro do modal derrubaria o wizard inteiro —
            medido ao escrever o sentinela, e é a 5ª ocorrência da armadilha de modal sobre
            modal no projeto. O `empilhado` resolve a outra metade: os dois overlays têm
            `z-index: 1050`, e empate se decide pela ordem do DOM (`BUG-R97`).
          */}
          {escolhendoItensDe && (
            <EscolhaDeItensModal
              titulo={`Itens de ${escolhendoItensDe.razaoSocial}`}
              itens={itensEscolhiveis}
              marcados={itensDoFornecedor(escolhendoItensDe.cnpj).map(String)}
              onConfirmar={(ids) => {
                setItensPorFornecedor((atual) => ({ ...atual, [escolhendoItensDe.cnpj]: ids.map(Number) }));
                setEscolhendoItensDe(null);
              }}
              onFechar={() => setEscolhendoItensDe(null)}
            />
          )}
          {fornecedorEmCadastro && (
            <FornecedorDetalhesModal
              fornecedor={fornecedorEmCadastro}
              empilhado
              onFechar={() => setFornecedorEmCadastro(null)}
              onAtualizado={() => recarregarFornecedores(fornecedorEmCadastro.cnpj)}
            />
          )}
          {passo < 4 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {motivoBloqueio && <span className="pc-texto-secundario">{motivoBloqueio}</span>}
            <button
              type="button"
              className="pc-btn pc-btn--primario"
              disabled={!podeAvancar}
              onClick={() => setPasso((p) => (p + 1) as 2 | 3 | 4)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 18px' }}
            >
              Avançar &rarr;
            </button>
            </div>
          ) : (
            <button
              type="button"
              className="pc-btn pc-btn--primario"
              onClick={() => setConfirmando(true)}
              style={{ padding: '8px 18px' }}
            >
              Criar rascunho
            </button>
          )}
        </div>
      </div>

      <ConfirmacaoModal
        isOpen={confirmando}
        titulo="Criar o rascunho da cotação?"
        descricao={
          <>
            O rascunho fica salvo <strong>sem enviar nenhum convite</strong>. Os fornecedores só recebem o link
            quando você abrir a cotação e clicar em Enviar convites.
          </>
        }
        rotuloConfirmar="Criar rascunho"
        carregando={salvando}
        onConfirmar={salvar}
        onCancelar={() => setConfirmando(false)}
      />

    </div>
  );
};

interface FilterCotacoesModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onApplyFilters: (statuses: CotacaoStatus[]) => void;
  selectedStatuses: CotacaoStatus[];
}

const FilterCotacoesModal: React.FC<FilterCotacoesModalProps> = ({
  isOpen,
  onCancel,
  onApplyFilters,
  selectedStatuses: initialStatuses,
}) => {
  const [localStatuses, setLocalStatuses] = useState<CotacaoStatus[]>(initialStatuses);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalStatuses(initialStatuses);
    }
  }, [isOpen, initialStatuses]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const toggleStatus = (status: CotacaoStatus) => {
    setLocalStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleApply = () => {
    onApplyFilters(localStatuses);
  };

  const handleClear = () => {
    setLocalStatuses([]);
  };

  const statusesEmAndamento: { value: CotacaoStatus; label: string }[] = [
    { value: 'RASCUNHO', label: 'Rascunho' },
    { value: 'ENVIADA', label: 'Enviada' },
    { value: 'EM_RESPOSTA', label: 'Em resposta' },
    { value: 'EM_DECISAO', label: 'Em decisão' },
    { value: 'AGUARDANDO_APROVACAO', label: 'Aguardando aprovação' },
  ];

  const statusesFinais: { value: CotacaoStatus; label: string }[] = [
    { value: 'CONCLUIDA', label: 'Concluída' },
    { value: 'CANCELADA', label: 'Cancelada' },
  ];

  return (
    <div className="filter-car-modal-overlay">
      <div className="filter-car-modal-content" ref={modalRef} style={{ maxWidth: '600px' }}>
        <div className="filter-car-modal-header">
          <div className="header-info">
            <h2>Filtrar Cotações</h2>
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="filter-car-modal-body">
          <div className="filter-columns-container" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {/* Column 1: Em Andamento */}
            <div className="filter-column">
              <div className="filter-column-title">Em Andamento</div>
              <div className="filter-checkbox-group">
                {statusesEmAndamento.map((item) => {
                  const checked = localStatuses.includes(item.value);
                  return (
                    <label key={item.value} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatus(item.value)}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                      </div>
                      <span className="filter-text">{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Column 2: Finais */}
            <div className="filter-column">
              <div className="filter-column-title">Finais</div>
              <div className="filter-checkbox-group">
                {statusesFinais.map((item) => {
                  const checked = localStatuses.includes(item.value);
                  return (
                    <label key={item.value} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatus(item.value)}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                      </div>
                      <span className="filter-text">{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="filter-modal-actions">
            <button className="clear-button" onClick={handleClear} type="button">
              Limpar Filtros
            </button>
            <button className="apply-button" onClick={handleApply} type="button">
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tela T1 ───────────────────────────────────────────────────────────────────

const PortalCotacoesList: React.FC = () => {
  const navigate = useNavigate();
  const [cotacoes, setCotacoes] = useState<CotacaoListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState<CotacaoStatus[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [wizardAberto, setWizardAberto] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  /** Cotações esperando o gestor — o contador do botão "Aprovações" (28/10/2026). */
  const [aprovacoesPendentes, setAprovacoesPendentes] = useState(0);

  /*
   * O contador se atualiza ao abrir a tela, a cada aviso do Portal pelo socket (aprovação nova ou
   * veredito) e a cada minuto. Falha é `warning` com `toastId` fixo (regra da at-60 para
   * polling): sem o aviso, o botão sem número se leria como "não há nada para aprovar".
   */
  useEffect(() => {
    let ativo = true;
    const contar = async () => {
      try {
        const fila = await fetchFilaAprovacoes();
        if (ativo) setAprovacoesPendentes(fila.length);
      } catch {
        notify.warning('Não foi possível verificar as aprovações pendentes.', { toastId: 'portal-aprovacoes-contador' });
      }
    };
    contar();
    const intervalo = setInterval(contar, 60_000);
    window.addEventListener('portal:notificacao', contar);
    return () => {
      ativo = false;
      clearInterval(intervalo);
      window.removeEventListener('portal:notificacao', contar);
    };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await fetchCotacoes({
        status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
        search: buscaAplicada || undefined,
        pageSize: 50,
      });
      setCotacoes(resposta.data);
      setTotal(resposta.total);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [selectedStatuses, buscaAplicada]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      <div className="ConteDo recepcao-conte-do pc-page">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Portal de Cotações
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="recepcao-search-bar">
            <div className="ItemSearch">
              <img
                className="imgIcon"
                src={SearchIcon}
                alt="Ícone de busca"
                width="20"
                height="20"
              />
            </div>
            <input
              type="text"
              className="recepcao-search-input"
              placeholder="Pesquise por número, placa, veículo ou requisição"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          {/*
            O sino próprio do Portal saiu em 28/10/2026 (portal-cotacoes/at-14, pedido do Victor:
            "não gosto de dois sinos"): os avisos do módulo — aprovação esperando o gestor,
            proposta recebida, veredito, pedido com erro — aparecem no sino do topo, junto com os
            do SOAC e os de Requisições (`components/sino-notificacoes/fontesDeAvisos.ts`).
          */}
        </div>
      </div>

      <div className="VisualizarOrdenar" style={{ paddingRight: 0, paddingBottom: 20, width: '100%', justifyContent: 'space-between' }}>
        <div className="CtaBig MouseHover" onClick={() => setFilterModalOpen(true)} style={{ cursor: 'pointer', marginRight: 0 }}>
          <div className="Frame1000003987">
            <img src={FilterIcon} alt="Filtro" width="19.80" height="19.80" />
            <div className="ComponentName">Filtrar</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/*
            Acesso às Aprovações (13/08/2026). A tela existia, funcionava e tinha rota e helper
            em routes.ts desde o MVP, mas NENHUM lugar da interface navegava até ela — só a URL
            digitada, do mesmo jeito que Logs de Peças. Com isso a cotação parava em "Aguardando
            aprovação" sem caminho adiante, que é um beco sem saída no caminho feliz.
            Fica aqui, e não na Sidebar, por decisão do usuário: a audiência é a mesma da
            listagem e a navegação do módulo permanece autocontida.

            Contador de pendências desde 28/10/2026 (pedido do Victor, que reverte o "sem
            contador" de 13/08): o número de cotações esperando o gestor, para a fila não passar
            despercebida. Sem pendência, o botão fica como sempre foi.
          */}
          <div
            className="CtaBig MouseHover"
            onClick={() => navigate(PORTAL_COTACOES_APROVACOES())}
            style={{ cursor: 'pointer', marginRight: 0 }}
            title={
              aprovacoesPendentes
                ? `${aprovacoesPendentes} ${aprovacoesPendentes === 1 ? 'cotação aguardando' : 'cotações aguardando'} aprovação`
                : undefined
            }
          >
            <div className="Frame1000003987">
              <div className="ComponentName">Aprovações</div>
              {aprovacoesPendentes > 0 && <span className="pc-contador-pendente">{aprovacoesPendentes}</span>}
            </div>
          </div>
          {/*
            Acesso à Supervisão (13/08/2026 — item 9 do backlog). Fica aqui, ao lado da ação
            principal, porque é a mesma audiência da listagem; a tela em si é só leitura.
          */}
          <div
            className="CtaBig MouseHover"
            onClick={() => navigate(PORTAL_COTACOES_SUPERVISAO())}
            style={{ cursor: 'pointer', marginRight: 0 }}
          >
            <div className="Frame1000003987">
              <div className="ComponentName">Supervisão</div>
            </div>
          </div>
          <div className="CtaSmall MouseHover" onClick={() => setWizardAberto(true)} style={{ marginRight: 0 }}>
            <div className="ComponentName">+ Nova cotação</div>
          </div>
        </div>
      </div>

      <div className="QuantidadeDeCarros" style={{ paddingBottom: 20 }}>
        <div
          className="Frame1000004037"
          style={{
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            display: 'flex',
          }}
        >
          <div className="CtaQuantidadeDeCarros">
            <div className="Frame1000003987">
              <div className="ComponentName" style={{ fontFamily: 'AmpleSoft-Bold', fontSize: '15px', color: 'var(--tema-texto, #1A1C1B)', border: 'none' }}>
                {carregando ? (
                  'Carregando...'
                ) : (
                  `1-${Math.min(50, total)} de ${total} cotaç${total === 1 ? 'ão' : 'ões'}`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
          <div
            className="TabelaContent"
            style={{
              ['--table-columns' as any]: '1fr 1.2fr 1.8fr 1.5fr 1.2fr 1.5fr 1.2fr 1fr',
              width: '100%',
              minWidth: '950px',
            }}
          >
            <div className="Tabela TabelaHeader">
              <div className="Placa">
                <div className="TTulo">
                  <div className="Opcoes">Número</div>
                  <div className="Opcoes">Placa</div>
                  <div className="Opcoes">Veículo</div>
                  <div className="Opcoes">Comprador</div>
                  <div className="Opcoes">Status</div>
                  <div className="Opcoes">Respostas</div>
                  <div className="Opcoes">Prazo</div>
                  <div className="Opcoes">Criada em</div>
                </div>
              </div>
            </div>

            {carregando ? (
              <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                <div className="Item" style={{ gridColumn: '1 / span 8', justifyContent: 'center', padding: '40px 0' }}>
                  <div className="Data" style={{ textAlign: 'center' }}>Carregando cotações...</div>
                </div>
              </div>
            ) : cotacoes.length === 0 ? (
              <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                <div className="Item" style={{ gridColumn: '1 / span 8', justifyContent: 'center', padding: '40px 0' }}>
                  <div className="Data" style={{ textAlign: 'center' }}>Nenhuma cotação encontrada.</div>
                </div>
              </div>
            ) : (
              cotacoes.map((c) => (
                <div
                  key={c.id}
                  className="Tabela"
                  style={{ paddingLeft: 30, paddingRight: 30 }}
                >
                  <div className="Item">
                    <span
                      className="pc-numero-cotacao"
                      style={{ fontSize: '12px' }}
                      onClick={() => navigate(`/portal-cotacoes/${c.id}`)}
                    >
                      {c.numero}
                    </span>
                  </div>
                  <div className="Item">
                    <span className="Data" style={{ fontSize: '12px' }}>{c.placa ?? '—'}</span>
                  </div>
                  <div className="Item">
                    <span className="Data" style={{ fontSize: '12px' }}>{c.veiculo ?? '—'}</span>
                  </div>
                  <div className="Item">
                    <span className="Data">{c.compradorNome ?? '—'}</span>
                  </div>
                  <div className="Item">
                    <CotacaoBadge status={c.status} />
                  </div>
                  <div className="Item">
                    <span className="Data">
                      {c.respostas.convidados > 0 ? (
                        `${c.respostas.responderam} de ${c.respostas.convidados} responderam`
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                  <div className="Item">
                    <span className="Data">{formatarDataHora(c.prazoRespostaAte)}</span>
                    {prazoVencido(c.status, c.prazoRespostaAte) && (
                      <>
                        {' '}
                        <PrazoVencidoBadge />
                      </>
                    )}
                  </div>
                  <div className="Item">
                    <span className="Data">{formatarData(c.createdAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {wizardAberto && (
        <WizardNovaCotacao
          onFechar={() => setWizardAberto(false)}
          onCriada={(id) => {
            setWizardAberto(false);
            navigate(`/portal-cotacoes/${id}`);
          }}
        />
      )}

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Portal de Cotações"
        secoes={helpSections}
      />

      <FilterCotacoesModal
        isOpen={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        onApplyFilters={(statuses) => {
          setSelectedStatuses(statuses);
          setFilterModalOpen(false);
        }}
        selectedStatuses={selectedStatuses}
      />
      </div>
    </div>
  );
};

export default PortalCotacoesList;
